import React, { useState } from 'react';
import { Task, TaskComment } from '../../types/task';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { ChatBubbleLeftIcon, TrashIcon, PaperAirplaneIcon, PencilIcon, ExclamationTriangleIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import Modal from '../common/Modal';
import { CommentAttachmentsList, CommentFileUpload } from './CommentAttachments';

interface TaskCommentsProps {
  task: Task;
  onAddComment: (message: string, files?: File[]) => Promise<void>;
  onDeleteComment: (commentId: string) => Promise<void>;
  onUpdateComment?: (commentId: string, message: string) => Promise<void>;
  onDeleteAttachment?: (commentId: string, attachmentId: string) => Promise<void>;
}

const TaskComments: React.FC<TaskCommentsProps> = ({ task, onAddComment, onDeleteComment, onUpdateComment, onDeleteAttachment }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [newComment, setNewComment] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingMessage, setEditingMessage] = useState('');
  const [commentToDelete, setCommentToDelete] = useState<TaskComment | null>(null);
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newComment.trim() && selectedFiles.length === 0) {
      toast.error('A megjegyzés vagy fájl megadása kötelező');
      return;
    }

    setIsSubmitting(true);
    try {
      await onAddComment(newComment.trim(), selectedFiles.length > 0 ? selectedFiles : undefined);
      setNewComment('');
      setSelectedFiles([]);
      toast.success(t('tasks.comments.addSuccess') || 'Megjegyzés hozzáadva');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error(t('tasks.comments.addError') || 'Hiba a megjegyzés hozzáadásakor');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (comment: TaskComment) => {
    setCommentToDelete(comment);
  };

  const handleDeleteConfirm = async () => {
    if (!commentToDelete) return;

    try {
      await onDeleteComment(commentToDelete.id);
      toast.success(t('tasks.comments.deleteSuccess') || 'Megjegyzés törölve');
      setCommentToDelete(null);
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error(t('tasks.comments.deleteError') || 'Hiba a megjegyzés törlésekor');
    }
  };

  const handleDeleteCancel = () => {
    setCommentToDelete(null);
  };

  const handleEdit = (comment: TaskComment) => {
    setEditingCommentId(comment.id);
    setEditingMessage(comment.message);
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditingMessage('');
  };

  const handleUpdateSubmit = async (commentId: string) => {
    if (!editingMessage.trim()) {
      toast.error(t('tasks.comments.emptyMessage') || 'A megjegyzés nem lehet üres');
      return;
    }

    if (!onUpdateComment) return;

    try {
      await onUpdateComment(commentId, editingMessage.trim());
      setEditingCommentId(null);
      setEditingMessage('');
      toast.success(t('tasks.comments.editSuccess') || 'Megjegyzés frissítve');
    } catch (error) {
      console.error('Error updating comment:', error);
      toast.error(t('tasks.comments.editError') || 'Hiba a megjegyzés frissítésekor');
    }
  };

  const handleDeleteAttachment = async (commentId: string, attachmentId: string) => {
    if (!onDeleteAttachment) return;
    
    try {
      await onDeleteAttachment(commentId, attachmentId);
      toast.success('Melléklet törölve');
    } catch (error) {
      console.error('Error deleting attachment:', error);
      toast.error('Hiba a melléklet törlésekor');
    }
  };

  const toggleCommentExpanded = (commentId: string) => {
    setExpandedComments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(commentId)) {
        newSet.delete(commentId);
      } else {
        newSet.add(commentId);
      }
      return newSet;
    });
  };

  const shouldTruncateComment = (message: string): boolean => {
    const lines = message.split('\n');
    return lines.length > 10 || message.length > 500;
  };

  const getTruncatedMessage = (message: string): string => {
    const lines = message.split('\n');
    if (lines.length > 10) {
      return lines.slice(0, 10).join('\n');
    }
    if (message.length > 500) {
      return message.substring(0, 500);
    }
    return message;
  };

  const formatDate = (date: any) => {
    try {
      let dateObj: Date;
      if (date instanceof Date) {
        dateObj = date;
      } else if (typeof date === 'object' && date.toDate) {
        dateObj = date.toDate();
      } else {
        dateObj = new Date(date);
      }

      return new Intl.DateTimeFormat('hu-HU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(dateObj);
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Dátum hiba';
    }
  };

  const comments = task.comments || [];

  return (
    <div className="space-y-4">
      {/* Komment lista - időmérő stílusban */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white">
            {t('tasks.sections.comments') || 'Megjegyzések'} ({comments.length})
          </h4>
        </div>
        
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {comments.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
              <ChatBubbleLeftIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>{t('tasks.comments.empty') || 'Még nincsenek megjegyzések'}</p>
            </div>
          ) : (
            comments.map((comment) => (
              <div key={comment.id} className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-white text-xs font-bold">
                        {comment.userName.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {comment.userName}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(comment.createdAt)}
                        </p>
                      </div>
                    </div>
                    
                    {editingCommentId === comment.id ? (
                      <div className="mt-2 space-y-2">
                        <textarea
                          value={editingMessage}
                          onChange={(e) => setEditingMessage(e.target.value)}
                          rows={4}
                          className="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-500/20 dark:focus:ring-blue-400/20 transition-all"
                        />
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={handleCancelEdit}
                            className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                          >
                            {t('common.cancel') || 'Mégse'}
                          </button>
                          <button
                            onClick={() => handleUpdateSubmit(comment.id)}
                            className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                          >
                            {t('common.save') || 'Mentés'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {comment.message && (
                          <div className="mt-1">
                            <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap break-words">
                              {shouldTruncateComment(comment.message) && !expandedComments.has(comment.id)
                                ? getTruncatedMessage(comment.message)
                                : comment.message}
                            </p>
                            {shouldTruncateComment(comment.message) && (
                              <button
                                onClick={() => toggleCommentExpanded(comment.id)}
                                className="mt-2 inline-flex items-center text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors"
                              >
                                {expandedComments.has(comment.id) ? (
                                  <>
                                    <ChevronUpIcon className="h-3 w-3 mr-1" />
                                    Kevesebb
                                  </>
                                ) : (
                                  <>
                                    <ChevronDownIcon className="h-3 w-3 mr-1" />
                                    Több... ({comment.message.split('\n').length} sor)
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        )}
                        {comment.attachments && comment.attachments.length > 0 && (
                          <CommentAttachmentsList
                            attachments={comment.attachments}
                            onDelete={onDeleteAttachment ? (attachmentId) => handleDeleteAttachment(comment.id, attachmentId) : undefined}
                            canDelete={user?.id === comment.userId}
                          />
                        )}
                      </>
                    )}
                  </div>
                  
                  {user?.id === comment.userId && editingCommentId !== comment.id && (
                    <div className="flex items-center space-x-2 ml-4">
                      {onUpdateComment && (
                        <button
                          onClick={() => handleEdit(comment)}
                          className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                          title={t('tasks.comments.edit') || 'Szerkesztés'}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteClick(comment)}
                        className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                        title={t('tasks.comments.delete') || 'Törlés'}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Új komment form - időmérő stílusban */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <form onSubmit={handleSubmit} className="space-y-3">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder={t('tasks.comments.placeholder') || 'Írj megjegyzést...'}
            rows={4}
            className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-500/20 dark:focus:ring-blue-400/20 transition-all"
            disabled={isSubmitting}
          />
          
          {/* Fájlfeltöltő */}
          <CommentFileUpload
            onFilesSelected={setSelectedFiles}
            maxFiles={20}
          />
          
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2">
            <button
              type="submit"
              disabled={isSubmitting || (!newComment.trim() && selectedFiles.length === 0)}
              className="flex items-center justify-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md md:rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base order-1 sm:order-2"
            >
              <PaperAirplaneIcon className="h-3.5 w-3.5 md:h-4 md:w-4" />
              <span>{isSubmitting ? (t('tasks.comments.sending') || 'Küldés...') : (t('tasks.comments.send') || 'Küldés')}</span>
            </button>
            <p className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400 order-2 sm:order-1">
              {t('tasks.comments.hint') || 'Csatolhatsz képeket, PDF-eket és dokumentumokat'}
            </p>
          </div>
        </form>
      </div>

      {/* Delete Confirmation Modal - időmérő stílusban */}
      {commentToDelete && (
        <Modal
          isOpen={!!commentToDelete}
          onClose={handleDeleteCancel}
          title=""
          size="lg"
          zIndex={70}
        >
          <div className="-m-6 mb-6 px-6 py-5 bg-white dark:bg-gray-800 border-b-2 border-gray-100 dark:border-gray-700">
            <div className="flex items-center space-x-4">
              <div className="p-2.5 bg-gradient-to-br from-red-500 to-orange-600 rounded-xl shadow-lg">
                <ExclamationTriangleIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {t('tasks.comments.delete') || 'Megjegyzés törlése'}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  Ez a művelet nem vonható vissza
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border-2 border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold">
                  {commentToDelete.userName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {commentToDelete.userName}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatDate(commentToDelete.createdAt)}
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                {commentToDelete.message}
              </p>
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t-2 border-gray-100 dark:border-gray-700">
              <button
                onClick={handleDeleteCancel}
                className="px-6 py-3 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all"
              >
                {t('common.cancel') || 'Mégse'}
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-6 py-3 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all"
              >
                {t('common.delete') || 'Törlés'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default TaskComments;
